import { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors } from 'react-hook-form';
import { CreateCandidatoDto } from './candidato.service';
import { ReadProcessoSeletivoDto } from '../processo-seletivo/processo-seletivo.service';
import { ReadComarcaDto, ReadDistritoDto, ReadInstituicaoEnsinoDto } from '@/api.types';
import { MunicipioIBGE } from '@/modules/structure/municipio/municipio.service';
import { MutableRefObject } from 'react';
import { validaCPF, isValidCPF } from '@/shared/utils/cpfs';

interface FormularioInscricaoProps {
  register: UseFormRegister<CreateCandidatoDto>;
  setValue: UseFormSetValue<CreateCandidatoDto>;
  watch: UseFormWatch<CreateCandidatoDto>;
  errors: FieldErrors<CreateCandidatoDto>;
  isLoadingCEP: boolean;
  setIsLoadingCEP: (loading: boolean) => void;
  buscarCEP: (cep: string) => Promise<void>;
  telefoneNumeroRef: MutableRefObject<HTMLInputElement | null>;
  celularNumeroRef: MutableRefObject<HTMLInputElement | null>;
  processoSeletivo: ReadProcessoSeletivoDto;
  instituicoesEnsino: ReadInstituicaoEnsinoDto[];
  comarcas: ReadComarcaDto[];
  distritos: ReadDistritoDto[];
  cursoPretendidoId: number | undefined;
  comarcaPretendidaId: number | undefined;
  pessoaComDeficiencia: boolean | undefined;
  optaVagasPcd: boolean | undefined;
  declaraIndigena: boolean | undefined;
  raca: 'branca' | 'preta' | 'parda' | 'amarela' | 'indigena' | undefined;
  instituicaoEnsinoIdStr: string;
  comarcasDisponiveis: number[];
  distritosDisponiveis: number[];
  municipios: MunicipioIBGE[];
  onFileValidationError: (message: string) => void;
  handleViewFile: (fileId: string) => Promise<void>;
  handleDownloadFile: (fileId: string, filename: string) => Promise<void>;
}

export default function FormularioInscricao({
  register,
  setValue,
  watch,
  errors,
  isLoadingCEP,
  setIsLoadingCEP,
  buscarCEP,
  telefoneNumeroRef,
  celularNumeroRef,
  processoSeletivo,
  instituicoesEnsino,
  pessoaComDeficiencia,
  optaVagasPcd,
  raca,
  instituicaoEnsinoIdStr,
  municipios,
  onFileValidationError,
  handleViewFile,
  handleDownloadFile,
}: FormularioInscricaoProps) {
  // Função auxiliar para obter classe CSS de erro
  const getErrorClass = (fieldName: string): string => {
    const fieldError = getNestedError(errors, fieldName);
    return fieldError ? 'form-control is-invalid' : 'form-control';
  };

  // Função auxiliar para obter classe CSS de erro para select
  const getSelectErrorClass = (fieldName: string): string => {
    const fieldError = getNestedError(errors, fieldName);
    return fieldError ? 'form-select is-invalid' : 'form-select';
  };

  // Função auxiliar para obter mensagem de erro
  const getErrorMessage = (fieldName: string): string | undefined => {
    const fieldError = getNestedError(errors, fieldName);
    return fieldError?.message;
  };

  // Função auxiliar para acessar erros aninhados (ex: endereco.cep)
  const getNestedError = (errorsObj: FieldErrors<CreateCandidatoDto>, fieldPath: string): any => {
    const parts = fieldPath.split('.');
    let current: any = errorsObj;
    for (const part of parts) {
      if (current && current[part]) {
        current = current[part];
      } else {
        return undefined;
      }
    }
    return current;
  };

  const MAX_SIZE_ARQUIVO_BYTES = 5 * 1024 * 1024; // 5MB

  // Função para validar se o arquivo é PDF e está dentro do tamanho máximo
  const validarArquivoPDF = (file: File | null, campoNome: string): boolean => {
    if (!file) return false;

    if (file.size > MAX_SIZE_ARQUIVO_BYTES) {
      onFileValidationError('Só é permitido anexar arquivo com no máximo 5MB.');
      return false;
    }

    // Verificar extensão do arquivo
    const extensao = file.name.toLowerCase().split('.').pop();
    if (extensao !== 'pdf') {
      onFileValidationError(`Apenas arquivos do tipo PDF são válidos para o campo "${campoNome}".`);
      return false;
    }

    // Verificar tipo MIME
    if (file.type && file.type !== 'application/pdf') {
      onFileValidationError(`Apenas arquivos do tipo PDF são válidos para o campo "${campoNome}".`);
      return false;
    }

    return true;
  };

  return (
    <>
      {/* DADOS PESSOAIS */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Identificação Pessoal</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-7">
              <label htmlFor="nome" className="form-label">Nome Completo: *</label>
              <input
                id="nome"
                className={getErrorClass('nome')}
                required
                {...register('nome', { required: 'Nome Completo é obrigatório' })}
              />
              {getErrorMessage('nome') && (
                <div className="invalid-feedback">
                  {getErrorMessage('nome')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-5">
              <label htmlFor="nomeSocial" className="form-label">Nome Social <small className="text-muted">(opcional)</small></label>
              <input
                id="nomeSocial"
                className="form-control"
                {...register('nomeSocial')}
              />
            </div>

            <div className="mb-3 col-md-4">
              <label htmlFor="cpf" className="form-label">CPF: *</label>
              <input
                id="cpf"
                className={getErrorClass('cpf')}
                maxLength={14}
                required
                {...register('cpf', {
                  onChange: (e) => {
                    const value = e.target.value;
                    setValue('cpf', validaCPF(value, true), { shouldValidate: true });
                  },
                  validate: (value) => {
                    if (!value) {
                      return 'CPF é obrigatório';
                    }
                    const cleaned = value.replace(/\D/g, '');
                    if (cleaned.length !== 11) {
                      return 'CPF deve conter 11 dígitos';
                    }
                    if (!isValidCPF(value)) {
                      return 'CPF inválido. Verifique os dígitos informados.';
                    }
                    return true;
                  },
                })}
              />
              {getErrorMessage('cpf') && (
                <div className="invalid-feedback">
                  {getErrorMessage('cpf')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-4">
              <label htmlFor="dataNascimento" className="form-label">Data de Nascimento: *</label>
              <input
                id="dataNascimento"
                type="date"
                className={getErrorClass('dataNascimento')}
                required
                {...register('dataNascimento', { required: 'Data de Nascimento é obrigatória' })}
              />
              {getErrorMessage('dataNascimento') && (
                <div className="invalid-feedback">
                  {getErrorMessage('dataNascimento')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-4">
              <label htmlFor="genero" className="form-label">Gênero: *</label>
              <select
                id="genero"
                className={getSelectErrorClass('genero')}
                required
                {...register('genero', { required: 'Gênero é obrigatório' })}
              >
                <option value="">Selecione...</option>
                <option value="masculino">Masculino</option>
                <option value="feminino">Feminino</option>
              </select>
              {getErrorMessage('genero') && (
                <div className="invalid-feedback">
                  {getErrorMessage('genero')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CONTATOS */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Contatos</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-3">
              <label htmlFor="email" className="form-label">E-mail: *</label>
              <input
                id="email"
                type="email"
                className={getErrorClass('email')}
                required
                {...register('email', {
                  required: 'E-mail é obrigatório',
                  onChange: () => {
                    // Limpar confirmação de email quando o email principal mudar
                    setValue('emailConfirmacao', '', { shouldValidate: true });
                  }
                })}
              />
              {getErrorMessage('email') && (
                <div className="invalid-feedback">
                  {getErrorMessage('email')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="emailConfirmacao" className="form-label">Confirme o E-mail: *</label>
              <input
                id="emailConfirmacao"
                type="email"
                className={getErrorClass('emailConfirmacao')}
                required
                autoComplete="off"
                onPaste={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onContextMenu={(e) => {
                  // Desabilitar menu de contexto (botão direito) para evitar colar
                  e.preventDefault();
                  return false;
                }}
                onKeyDown={(e) => {
                  // Bloquear Ctrl+V, Ctrl+Shift+V, Ctrl+Insert, Shift+Insert
                  if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V' || e.key === 'Insert')) {
                    e.preventDefault();
                    return false;
                  }
                  if (e.shiftKey && e.key === 'Insert') {
                    e.preventDefault();
                    return false;
                  }
                }}
                {...register('emailConfirmacao', {
                  required: 'Confirmação de e-mail é obrigatória',
                  validate: (value) => {
                    const email = watch('email');
                    if (!value) {
                      return 'Por favor, confirme o e-mail';
                    }
                    if (value !== email) {
                      return 'Os e-mails não coincidem';
                    }
                    return true;
                  }
                })}
              />
              {getErrorMessage('emailConfirmacao') && (
                <div className="invalid-feedback">
                  {getErrorMessage('emailConfirmacao')}
                </div>
              )}
              {watch('emailConfirmacao') && watch('emailConfirmacao') !== watch('email') && !getErrorMessage('emailConfirmacao') && (
                <div className="text-danger small mt-1">
                  Os e-mails não coincidem
                </div>
              )}
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="telefone" className="form-label">Telefone <small className="text-muted">(opcional)</small></label>
              <div className="phone-input-compact">
                <input
                  id="telefone-ddd"
                  type="text"
                  className="phone-ddd-compact"
                  placeholder="DDD"
                  maxLength={2}
                  {...register('telefone.ddd', {
                    onChange: (e) => {
                      const value = e.target.value.replace(/\D/g, '').substring(0, 2);
                      setValue('telefone.ddd', value, { shouldValidate: true });
                      if (value.length === 2 && telefoneNumeroRef.current) {
                        telefoneNumeroRef.current.focus();
                      }
                    },
                  })}
                />
                <input
                  id="telefone-numero"
                  type="text"
                  className="phone-number-compact"
                  placeholder="0000-0000"
                  maxLength={9}
                  {...register('telefone.numero', {
                    onChange: (e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      let formatted = value;
                      if (value.length <= 4) {
                        formatted = value;
                      } else if (value.length <= 8) {
                        formatted = value.substring(0, 4) + '-' + value.substring(4, 8);
                      } else {
                        formatted = value.substring(0, 4) + '-' + value.substring(4, 8);
                      }
                      setValue('telefone.numero', formatted, { shouldValidate: true });
                    },
                  })}
                  ref={(e) => {
                    const { ref } = register('telefone.numero');
                    ref(e);
                    telefoneNumeroRef.current = e;
                  }}
                />
              </div>
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="celular" className="form-label">Celular *</label>
              <div className="phone-input-compact">
                <input
                  id="celular-ddd"
                  type="text"
                  className={`phone-ddd-compact ${getNestedError(errors, 'celular.ddd') ? 'is-invalid' : ''}`}
                  placeholder="DDD"
                  maxLength={2}
                  {...register('celular.ddd', {
                    required: 'DDD do celular é obrigatório',
                    onChange: (e) => {
                      const value = e.target.value.replace(/\D/g, '').substring(0, 2);
                      setValue('celular.ddd', value, { shouldValidate: true });
                      if (value.length === 2 && celularNumeroRef.current) {
                        celularNumeroRef.current.focus();
                      }
                    },
                  })}
                />
                <input
                  id="celular-numero"
                  type="text"
                  className={`phone-number-compact ${getNestedError(errors, 'celular.numero') ? 'is-invalid' : ''}`}
                  placeholder="00000-0000"
                  maxLength={10}
                  {...register('celular.numero', {
                    required: 'Número do celular é obrigatório',
                    onChange: (e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      let formatted = value;
                      if (value.length <= 5) {
                        formatted = value;
                      } else if (value.length <= 9) {
                        formatted = value.substring(0, 5) + '-' + value.substring(5, 9);
                      } else {
                        formatted = value.substring(0, 5) + '-' + value.substring(5, 9);
                      }
                      setValue('celular.numero', formatted, { shouldValidate: true });
                    },
                  })}
                  ref={(e) => {
                    const { ref } = register('celular.numero');
                    ref(e);
                    celularNumeroRef.current = e;
                  }}
                />
              </div>
              {(getErrorMessage('celular.ddd') || getErrorMessage('celular.numero')) && (
                <div className="invalid-feedback d-block">
                  {getErrorMessage('celular.ddd') || getErrorMessage('celular.numero')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ENDEREÇO */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Endereço</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-3">
              <label htmlFor="cep" className="form-label">CEP *</label>
              <div className="position-relative">
                <input
                  id="cep"
                  className={getErrorClass('endereco.cep')}
                  placeholder="00000-000"
                  maxLength={9}
                  {...register('endereco.cep', {
                    required: 'CEP é obrigatório',
                    onChange: async (e) => {
                      const inputValue = e.target.value;
                      let value = inputValue.replace(/\D/g, '');

                      // Formata CEP com máscara 00000-000
                      let cepFormatado = value;
                      if (value.length > 5) {
                        cepFormatado = value.substring(0, 5) + '-' + value.substring(5, 8);
                      }

                      // Atualiza o valor formatado no formulário
                      setValue('endereco.cep', cepFormatado, { shouldValidate: true });

                      // Busca CEP quando tiver 8 dígitos (sem a máscara)
                      if (value.length === 8) {
                        await buscarCEP(value);
                      } else if (value.length < 8) {
                        // Limpar campos quando CEP for incompleto
                        setIsLoadingCEP(false);
                        setValue('endereco.logradouro', '');
                        setValue('endereco.bairro', '');
                        setValue('endereco.cidade', '');
                        setValue('endereco.estado', '');
                      }
                    },
                  })}
                  style={{ paddingRight: isLoadingCEP ? '2.5rem' : undefined }}
                />
                {isLoadingCEP && (
                  <div className="position-absolute top-50 end-0 translate-middle-y pe-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                )}
                {getErrorMessage('endereco.cep') && (
                  <div className="invalid-feedback">
                    {getErrorMessage('endereco.cep')}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="logradouro" className="form-label">Logradouro *</label>
              <input
                id="logradouro"
                className={getErrorClass('endereco.logradouro')}
                {...register('endereco.logradouro', {
                  required: 'Logradouro é obrigatório',
                })}
              />
              {getErrorMessage('endereco.logradouro') && (
                <div className="invalid-feedback">
                  {getErrorMessage('endereco.logradouro')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-3">
              <label htmlFor="numero" className="form-label">Número *</label>
              <input
                id="numero"
                type="number"
                className={getErrorClass('endereco.numero')}
                {...register('endereco.numero', {
                  required: 'Número é obrigatório',
                  valueAsNumber: true,
                })}
              />
              {getErrorMessage('endereco.numero') && (
                <div className="invalid-feedback">
                  {getErrorMessage('endereco.numero')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-4">
              <label htmlFor="complemento" className="form-label">Complemento *</label>
              <input
                id="complemento"
                className={getErrorClass('endereco.complemento')}
                {...register('endereco.complemento', {
                  required: 'Complemento é obrigatório',
                })}
              />
              {getErrorMessage('endereco.complemento') && (
                <div className="invalid-feedback">
                  {getErrorMessage('endereco.complemento')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-4">
              <label htmlFor="bairro" className="form-label">Bairro *</label>
              <input
                id="bairro"
                className={getErrorClass('endereco.bairro')}
                {...register('endereco.bairro', {
                  required: 'Bairro é obrigatório',
                })}
              />
              {getErrorMessage('endereco.bairro') && (
                <div className="invalid-feedback">
                  {getErrorMessage('endereco.bairro')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-2">
              <label htmlFor="cidade" className="form-label">Cidade *</label>
              <input
                id="cidade"
                className={getErrorClass('endereco.cidade')}
                {...register('endereco.cidade', {
                  required: 'Cidade é obrigatória',
                })}
              />
              {getErrorMessage('endereco.cidade') && (
                <div className="invalid-feedback">
                  {getErrorMessage('endereco.cidade')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-2">
              <label htmlFor="estado" className="form-label">UF *</label>
              <input
                id="estado"
                className={getErrorClass('endereco.estado')}
                maxLength={2}
                {...register('endereco.estado', {
                  required: 'Estado (UF) é obrigatório',
                })}
              />
              {getErrorMessage('endereco.estado') && (
                <div className="invalid-feedback">
                  {getErrorMessage('endereco.estado')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Dados Acadêmicos</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label htmlFor="instituicaoEnsinoId" className="form-label">Instituição de Ensino *</label>
              <select
                id="instituicaoEnsinoId"
                className="form-select"
                value={instituicaoEnsinoIdStr}
                {...register('instituicaoEnsinoId', {
                  validate: (value) => {
                    if (value == null || value === undefined) return 'Selecione uma instituição de ensino ou escolha "Outros"';
                    if (typeof value === 'string' && String(value).trim() === '') return 'Selecione uma instituição de ensino ou escolha "Outros"';
                    return true;
                  },
                  onChange: (e) => {
                    const value = e.target.value;
                    if (value && value !== 'outros' && value !== '') {
                      setValue('instituicaoEnsinoNome', undefined as any);
                      const idNumero = Number(value);
                      if (!isNaN(idNumero)) {
                        setValue('instituicaoEnsinoId', idNumero, { shouldValidate: true });
                      }
                    } else if (value === 'outros') {
                      setValue('instituicaoEnsinoId', 'outros' as any, { shouldValidate: true });
                    } else {
                      setValue('instituicaoEnsinoId', undefined as any, { shouldValidate: true });
                      setValue('instituicaoEnsinoNome', undefined as any);
                    }
                  }
                })}
              >
                <option value="">Selecione...</option>
                <option className='body-teste' value="outros">Outros</option>
                {instituicoesEnsino.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.razao_social}
                  </option>
                ))}
              </select>
            </div>

            {instituicaoEnsinoIdStr === 'outros' && (
              <div className="mb-3 col-md-6">
                <label htmlFor="instituicaoEnsinoNome" className="form-label">Nome da Instituição de Ensino *</label>
                <input
                  id="instituicaoEnsinoNome"
                  type="text"
                  className={getErrorClass('instituicaoEnsinoNome')}
                  required
                  {...register('instituicaoEnsinoNome', { required: 'Nome da Instituição de Ensino é obrigatório' })}
                  placeholder="Digite o nome da instituição"
                />
                {getErrorMessage('instituicaoEnsinoNome') && (
                  <div className="invalid-feedback">
                    {getErrorMessage('instituicaoEnsinoNome')}
                  </div>
                )}
              </div>
            )}

            <div className="mb-3 col-md-6">
              <label htmlFor="periodoAtualCurso" className="form-label">Período Atual do Curso *</label>
              <input
                id="periodoAtualCurso"
                type="number"
                min="1"
                className={getErrorClass('periodoAtualCurso')}
                {...register('periodoAtualCurso', {
                  required: 'Período Atual do Curso é obrigatório',
                  valueAsNumber: true
                })}
              />
              {getErrorMessage('periodoAtualCurso') && (
                <div className="invalid-feedback">
                  {getErrorMessage('periodoAtualCurso')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-6">
              <label htmlFor="anoInicioCurso" className="form-label">Ano de Início do Curso *</label>
              <input
                id="anoInicioCurso"
                type="number"
                className={getErrorClass('anoInicioCurso')}
                {...register('anoInicioCurso', {
                  required: 'Ano de Início do Curso é obrigatório',
                  valueAsNumber: true
                })}
              />
              {getErrorMessage('anoInicioCurso') && (
                <div className="invalid-feedback">
                  {getErrorMessage('anoInicioCurso')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CURSO, COMARCA E DISTRITO DE INTERESSE */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Curso, Comarca e Distrito de Interesse</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-4">
              <label htmlFor="cursoPretendidoId" className="form-label">Curso Pretendido: *</label>
              <select
                id="cursoPretendidoId"
                className={getSelectErrorClass('cursoPretendidoId')}
                required
                {...register('cursoPretendidoId', {
                  required: 'Curso Pretendido é obrigatório',
                  valueAsNumber: true
                })}
              >
                <option value="">Selecione...</option>
                {processoSeletivo.cursos?.map((curso) => (
                  <option key={curso.id} value={curso.id}>
                    {curso.nome}
                  </option>
                ))}
              </select>
              {getErrorMessage('cursoPretendidoId') && (
                <div className="invalid-feedback">
                  {getErrorMessage('cursoPretendidoId')}
                </div>
              )}
            </div>

            <div className="mb-3 col-md-4">
              <label htmlFor="municipio" className="form-label">Município: *</label>
              <select
                id="municipio"
                className={getSelectErrorClass('municipio')}
                required
                {...register('municipio', { required: 'Município é obrigatório' })}
              >
                <option value="">Selecione um município...</option>
                {municipios.map((municipio) => (
                  <option key={municipio.id} value={municipio.nome}>
                    {municipio.nome}
                  </option>
                ))}
              </select>
              {getErrorMessage('municipio') && (
                <div className="invalid-feedback">
                  {getErrorMessage('municipio')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* IDENTIFICAÇÃO ÉTNICO-RACIAL */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Identificação Étnico-Racial (Ações Afirmativas)</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label htmlFor="raca" className="form-label">Identificação Racial: *</label>
              <select
                id="raca"
                className={getSelectErrorClass('raca')}
                required
                {...register('raca', { required: 'Identificação Racial é obrigatória' })}
              >
                <option value="">Selecione...</option>
                <option value="branca">Branca</option>
                <option value="preta">Preta</option>
                <option value="parda">Parda (pessoa declarada como mulata, cabocla, cafuza, mameluca ou mestiça de negro com pessoa de outra cor ou raça)</option>
                <option value="amarela">Amarela (de origem japonesa, coreana etc.)</option>
                <option value="indigena">Indígena</option>
              </select>
              {getErrorMessage('raca') && (
                <div className="invalid-feedback">
                  {getErrorMessage('raca')}
                </div>
              )}
            </div>

            {raca && raca !== 'branca' && raca !== 'amarela' && (
              <div className="mb-3 col-md-6">
                <label className="form-label">
                  {raca === 'indigena'
                    ? 'Opta por concorrer às vagas reservadas para pessoas indígenas? *'
                    : 'Opta por concorrer às vagas reservadas para pessoas negras (pretos ou pardos)? *'
                  }
                </label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="optaVagasNegrosSim"
                      name="optaVagasNegros"
                      value="true"
                      checked={watch('optaVagasNegros') === true}
                      onChange={() => {
                        setValue('optaVagasNegros', true, { shouldValidate: true });
                      }}
                    />
                    <label className="form-check-label" htmlFor="optaVagasNegrosSim">
                      Sim
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="optaVagasNegrosNao"
                      name="optaVagasNegros"
                      value="false"
                      checked={watch('optaVagasNegros') === false}
                      onChange={() => {
                        setValue('optaVagasNegros', false, { shouldValidate: true });
                      }}
                    />
                    <label className="form-check-label" htmlFor="optaVagasNegrosNao">
                      Não
                    </label>
                  </div>
                </div>
                {raca !== 'indigena' && watch('optaVagasNegros') === true && (
                  <div className="mt-2">
                    <small className="text-muted">
                      O candidato que desejar concorrer às vagas reservadas às pessoas negras (pretas e pardas) participará de entrevista de heteroidentificação, onde será avaliada a autodeclaração de pessoa negra, forme instruções constantes no edital de abertura desta seleção.
                    </small>
                  </div>
                )}
              </div>
            )}



          </div>
        </div>
      </div>

      {/* PESSOA COM DEFICIÊNCIA */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Pessoa com Deficiência (PcD)</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label">Pessoa com deficiência? *</label>
              <div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="pessoaComDeficienciaSim"
                    name="pessoaComDeficiencia"
                    value="true"
                    checked={pessoaComDeficiencia === true}
                    onChange={() => {
                      setValue('pessoaComDeficiencia' as any, true, { shouldValidate: true });
                    }}
                  />
                  <label className="form-check-label" htmlFor="pessoaComDeficienciaSim">
                    Sim
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="pessoaComDeficienciaNao"
                    name="pessoaComDeficiencia"
                    value="false"
                    checked={pessoaComDeficiencia === false}
                    onChange={() => {
                      setValue('pessoaComDeficiencia' as any, false, { shouldValidate: true });
                      // Limpar campos relacionados quando selecionar "Não"
                      setValue('optaVagasPcd', undefined);
                      setValue('descricaoDeficiencia', undefined);
                      setValue('necessitaCuidadosEspeciais', undefined);
                      setValue('laudoMedicoUrl', undefined);
                    }}
                  />
                  <label className="form-check-label" htmlFor="pessoaComDeficienciaNao">
                    Não
                  </label>
                </div>
              </div>
            </div>

            {pessoaComDeficiencia === true && (
              <div className="mb-3 col-md-6">
                <label className="form-label">Opta por concorrer às vagas reservadas para pessoa com deficiência? *</label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="optaVagasPcdSim"
                      name="optaVagasPcd"
                      value="true"
                      checked={watch('optaVagasPcd') === true}
                      onChange={() => {
                        setValue('optaVagasPcd', true, { shouldValidate: true });
                      }}
                    />
                    <label className="form-check-label" htmlFor="optaVagasPcdSim">
                      Sim
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="optaVagasPcdNao"
                      name="optaVagasPcd"
                      value="false"
                      checked={watch('optaVagasPcd') === false}
                      onChange={() => {
                        setValue('optaVagasPcd', false, { shouldValidate: true });
                      }}
                    />
                    <label className="form-check-label" htmlFor="optaVagasPcdNao">
                      Não
                    </label>
                  </div>
                </div>
              </div>
            )}

            {optaVagasPcd === true && (
              <>
                <div className="mb-3 col-md-6">
                  <label htmlFor="descricaoDeficiencia" className="form-label">Descrição da deficiência *</label>
                  <textarea
                    id="descricaoDeficiencia"
                    className="form-control"
                    rows={3}
                    {...register('descricaoDeficiencia')}
                  />
                </div>

              </>
            )}
          </div>
        </div>
      </div>

      {/* UPLOAD DE DOCUMENTOS */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Upload de Documentos</h5>
        </div>
        <div className="card-body">
          <div className="row">
            {/* COLUNA ESQUERDA */}
            <div className="col-md-6">
              {/* 1. Documento de Identificação (Oficial) * */}
              <div className="mb-3">
                <label htmlFor="documentoIdentificacaoUrl" className="form-label">Documento de Identificação (Oficial) *</label>
                {watch('documentoIdentificacaoUrl') && typeof watch('documentoIdentificacaoUrl') === 'string' && (
                  <div className="mb-2">
                    <small className="text-success d-block mb-2">
                      Já existe um documento enviado. Selecione um novo arquivo para substituí-lo.
                    </small>
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleViewFile(watch('documentoIdentificacaoUrl') as string)}
                      >
                        Visualizar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadFile(watch('documentoIdentificacaoUrl') as string, 'documento_identificacao.pdf')}
                      >
                        Baixar
                      </button>
                    </div>
                  </div>
                )}
                <input
                  id="documentoIdentificacaoUrl"
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  required={!watch('documentoIdentificacaoUrl')}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (validarArquivoPDF(file, 'Documento de Identificação')) {
                        setValue('documentoIdentificacaoUrl', file as any);
                      } else {
                        e.target.value = ''; // Limpar o input
                      }
                    }
                  }}
                />
              </div>

              {/* 2. Comprovante de Residência * */}
              <div className="mb-3">
                <label htmlFor="comprovanteResidenciaUrl" className="form-label">Comprovante de Residência *</label>
                {watch('comprovanteResidenciaUrl') && typeof watch('comprovanteResidenciaUrl') === 'string' && (
                  <div className="mb-2">
                    <small className="text-success d-block mb-2">
                      Já existe um comprovante enviado. Selecione um novo arquivo para substituí-lo.
                    </small>
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleViewFile(watch('comprovanteResidenciaUrl') as string)}
                      >
                        Visualizar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadFile(watch('comprovanteResidenciaUrl') as string, 'comprovante_residencia.pdf')}
                      >
                        Baixar
                      </button>
                    </div>
                  </div>
                )}
                <input
                  id="comprovanteResidenciaUrl"
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  required={!watch('comprovanteResidenciaUrl')}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (validarArquivoPDF(file, 'Comprovante de Residência')) {
                        setValue('comprovanteResidenciaUrl', file as any);
                      } else {
                        e.target.value = ''; // Limpar o input
                      }
                    }
                  }}
                />
              </div>

              {/* 3. Laudo Médico (PCD) * - abaixo do Comprovante de Residência */}
              {optaVagasPcd && (
                <div className="mb-3">
                  <label htmlFor="laudoMedicoUrl" className="form-label">Laudo Médico (PCD) *</label>
                  {watch('laudoMedicoUrl') && typeof watch('laudoMedicoUrl') === 'string' && (
                    <div className="mb-2">
                      <small className="text-success d-block mb-2">
                        Já existe um laudo médico enviado. Selecione um novo arquivo para substituí-lo.
                      </small>
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleViewFile(watch('laudoMedicoUrl') as string)}
                        >
                          Visualizar
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleDownloadFile(watch('laudoMedicoUrl') as string, 'laudo_medico.pdf')}
                        >
                          Baixar
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    id="laudoMedicoUrl"
                    type="file"
                    className="form-control"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (validarArquivoPDF(file, 'Laudo Médico')) {
                          setValue('laudoMedicoUrl', file as any);
                        } else {
                          e.target.value = ''; // Limpar o input
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* COLUNA DIREITA */}
            <div className="col-md-6">
              {/* 1. Histórico Escolar (Graduação) * */}
              <div className="mb-3">
                <label htmlFor="documentoEstudoUrl" className="form-label">Histórico Escolar (Graduação) *</label>
                {watch('documentoEstudoUrl') && typeof watch('documentoEstudoUrl') === 'string' && (
                  <div className="mb-2">
                    <small className="text-success d-block mb-2">
                      Já existe um histórico escolar enviado. Selecione um novo arquivo para substituí-lo.
                    </small>
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleViewFile(watch('documentoEstudoUrl') as string)}
                      >
                        Visualizar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadFile(watch('documentoEstudoUrl') as string, 'documento_estudo.pdf')}
                      >
                        Baixar
                      </button>
                    </div>
                  </div>
                )}
                <input
                  id="documentoEstudoUrl"
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  required={!watch('documentoEstudoUrl')}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (validarArquivoPDF(file, 'Histórico Escolar (Graduação)')) {
                        setValue('documentoEstudoUrl', file as any);
                      } else {
                        e.target.value = ''; // Limpar o input
                      }
                    }
                  }}
                />
              </div>

              {/* 2. Documentos Complementares Escolar (opcional) - abaixo do Histórico Escolar */}
              <div className="mb-3">
                <label htmlFor="documentosComplementaresUrl" className="form-label">Documentos Complementares Escolar <small className="text-muted">(opcional)</small></label>
                {watch('documentosComplementaresUrl') && typeof watch('documentosComplementaresUrl') === 'string' && (
                  <div className="mb-2">
                    <small className="text-success d-block mb-2">
                      Já existe um documento complementar enviado. Selecione um novo arquivo para substituí-lo.
                    </small>
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => handleViewFile(watch('documentosComplementaresUrl') as string)}
                      >
                        Visualizar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadFile(watch('documentosComplementaresUrl') as string, 'documentos_complementares.pdf')}
                      >
                        Baixar
                      </button>
                    </div>
                  </div>
                )}
                <input
                  id="documentosComplementaresUrl"
                  type="file"
                  className="form-control"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (validarArquivoPDF(file, 'Documentos Complementares Escolar')) {
                        setValue('documentosComplementaresUrl', file as any);
                      } else {
                        e.target.value = ''; // Limpar o input
                      }
                    }
                  }}
                />
                <small className="text-muted d-block mt-1">
                  A declaração somente será necessária quando o histórico escolar não apresentar os dados solicitados.
                </small>
              </div>

              {/* 3. Documentação Indígena * */}
              {raca === 'indigena' && watch('optaVagasNegros') === true && (
                <div className="mb-3">
                  <label htmlFor="documentacaoIndigenaUrl" className="form-label">Documentação Indígena *</label>
                  {watch('documentacaoIndigenaUrl') && typeof watch('documentacaoIndigenaUrl') === 'string' && (
                    <div className="mb-2">
                      <small className="text-success d-block mb-2">
                        Já existe uma documentação indígena enviada. Selecione um novo arquivo para substituí-lo.
                      </small>
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleViewFile(watch('documentacaoIndigenaUrl') as string)}
                        >
                          Visualizar
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleDownloadFile(watch('documentacaoIndigenaUrl') as string, 'documentacao_indigena.pdf')}
                        >
                          Baixar
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    id="documentacaoIndigenaUrl"
                    type="file"
                    className="form-control"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (validarArquivoPDF(file, 'Documentação Indígena')) {
                          setValue('documentacaoIndigenaUrl', file as any);
                        } else {
                          e.target.value = ''; // Limpar o input
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* DECLARAÇÕES */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Declarações</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="declaracaoVeracidade"
                  required
                  {...register('declaracaoVeracidade')}
                />
                <label className="form-check-label" htmlFor="declaracaoVeracidade">
                  Declaração de veracidade das informações prestadas *
                </label>
              </div>
            </div>

            <div className="mb-3 col-md-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="confirmacaoEdital"
                  required
                  {...register('confirmacaoEdital')}
                />
                <label className="form-check-label" htmlFor="confirmacaoEdital">
                  Confirmação de ciência e concordância com o edital do processo seletivo *
                </label>
              </div>
            </div>

            <div className="mb-3 col-md-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="consentimentoLgpd"
                  required
                  {...register('consentimentoLgpd')}
                />
                <label className="form-check-label" htmlFor="consentimentoLgpd">
                  Consentimento para tratamento de dados pessoais, nos termos da Lei Geral de Proteção de Dados (LGPD) *
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

